import React from 'react';
import ReactMarkdown from 'react-markdown';
import { ChatMessage } from '../../types';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

interface ChatBubbleProps {
  message: ChatMessage;
  pendingConfirmation?: { action: string; summary: string } | null;
  onConfirm?: (confirmed: boolean) => void;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

const ChatBubble: React.FC<ChatBubbleProps> = ({ message, pendingConfirmation, onConfirm }) => {
  // Internal messages are retained in history for the LLM, not displayed in chat.
  if (message.role === 'system' || message.role === 'tool') return null;

  // User messages
  if (message.role === 'user') {
    return (
      <div className="flex justify-end mb-3">
        <div className="max-w-[85%] bg-primary-600 text-white rounded-lg rounded-br-sm px-3 py-2">
          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
          <p className="text-[10px] text-primary-200 mt-1 text-right">{formatTime(message.created_at)}</p>
        </div>
      </div>
    );
  }

  // Assistant messages
  return (
    <div className="flex justify-start mb-3">
      <div className="max-w-[85%]">
        <div className="bg-gray-100 rounded-lg rounded-bl-sm px-3 py-2">
          <div className="text-sm prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-headings:my-2 prose-pre:my-2 prose-code:text-xs">
            <ReactMarkdown>{message.content || ''}</ReactMarkdown>
          </div>
          <p className="text-[10px] text-gray-400 mt-1">{formatTime(message.created_at)}</p>
        </div>

        {/* Confirmation card */}
        {pendingConfirmation && onConfirm && (
          <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
            <p className="text-sm text-yellow-800 font-medium mb-2">{pendingConfirmation.summary}</p>
            <div className="flex space-x-2">
              <button
                onClick={() => onConfirm(true)}
                className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                <CheckCircleIcon className="h-3.5 w-3.5" />
                <span>Conferma</span>
              </button>
              <button
                onClick={() => onConfirm(false)}
                className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                <XCircleIcon className="h-3.5 w-3.5" />
                <span>Annulla</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatBubble;
